import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

export type ThemeMode = "light" | "dark";

export interface ThemePalette {
  background: string;
  backgroundAlt: string;
  card: string;
  cardAlt: string;
  border: string;
  borderSoft: string;
  textPrimary: string;
  textSecondary: string;
  textMuted: string;
  textOnAccent: string;
  accent: string;
  accentAlt: string;
  accentGradient: string;
  accentSoft: string;
  accentHover: string;
  inputBackground: string;
  inputBorder: string;
  shadow: string;
  overlay: string;
  success: string;
  danger: string;
  warning: string;
}

export interface ThemeDefinition {
  mode: ThemeMode;
  palette: ThemePalette;
  heroBackground: string;
}

const LIGHT_THEME: ThemeDefinition = {
  mode: "light",
  palette: {
    background: "#f4f8ff",
    backgroundAlt: "#e7f1ff",
    card: "#ffffff",
    cardAlt: "#f1f6ff",
    border: "#cfdcf3",
    borderSoft: "#dfe8f7",
    textPrimary: "#0f172a",
    textSecondary: "#1f2937",
    textMuted: "#546182",
    textOnAccent: "#ffffff",
    accent: "#2f91d1",
    accentAlt: "#65c0dd",
    accentGradient: "linear-gradient(90deg, #2f91d1 0%, #65c0dd 100%)",
    accentSoft: "rgba(47, 145, 209, 0.12)",
    accentHover: "#2781bc",
    inputBackground: "#ffffff",
    inputBorder: "#c4d7f6",
    shadow: "0 24px 48px rgba(15, 23, 42, 0.08)",
    overlay: "rgba(15, 23, 42, 0.35)",
    success: "#199f6f",
    danger: "#e25353",
    warning: "#f59e0b",
  },
  heroBackground:
    "linear-gradient(180deg, rgba(232, 241, 255, 0.95) 0%, #ffffff 120%)",
};

const DARK_THEME: ThemeDefinition = {
  mode: "dark",
  palette: {
    background: "#0b1120",
    backgroundAlt: "#111c32",
    card: "#111827",
    cardAlt: "#172033",
    border: "#1f2937",
    borderSoft: "#1c2a3d",
    textPrimary: "#f8fafc",
    textSecondary: "#e2e8f0",
    textMuted: "#93a3bf",
    textOnAccent: "#ffffff",
    accent: "#4fa3db",
    accentAlt: "#6ccff6",
    accentGradient: "linear-gradient(90deg, #4fa3db 0%, #6ccff6 100%)",
    accentSoft: "rgba(79, 163, 219, 0.2)",
    accentHover: "#3a90c9",
    inputBackground: "#0f172a",
    inputBorder: "#20324a",
    shadow: "0 24px 48px rgba(2, 6, 23, 0.35)",
    overlay: "rgba(2, 6, 23, 0.7)",
    success: "#34d399",
    danger: "#f87171",
    warning: "#fbbf24",
  },
  heroBackground: "linear-gradient(180deg, #0b1120 0%, #111827 100%)",
};

interface ThemeContextValue {
  theme: ThemeDefinition;
  mode: ThemeMode;
  setMode: (mode: ThemeMode) => void;
  toggleMode: () => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within ThemeProvider");
  return ctx;
}

interface ThemeProviderProps {
  children: React.ReactNode;
  defaultMode?: ThemeMode;
}

export function ThemeProvider({
  children,
  defaultMode = "light",
}: ThemeProviderProps) {
  const [mode, setMode] = useState<ThemeMode>(() => {
    if (typeof window !== "undefined") {
      const stored = window.localStorage.getItem(
        "launchSafelyTheme"
      ) as ThemeMode | null;
      if (stored === "light" || stored === "dark") {
        return stored;
      }
      if (typeof window.matchMedia === "function") {
        const prefersDark = window.matchMedia(
          "(prefers-color-scheme: dark)"
        ).matches;
        if (prefersDark) return "dark";
      }
    }
    return defaultMode;
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem("launchSafelyTheme", mode);
  }, [mode]);

  const theme = useMemo(
    () => (mode === "dark" ? DARK_THEME : LIGHT_THEME),
    [mode]
  );

  const value = useMemo(
    () => ({
      theme,
      mode,
      setMode,
      toggleMode: () =>
        setMode((prev) => (prev === "light" ? "dark" : "light")),
    }),
    [theme, mode]
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}

export const themes = {
  light: LIGHT_THEME,
  dark: DARK_THEME,
};
